import { Typography, useMediaQuery } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { toast } from "react-toastify";
import InfiniteScrollCardView from "./InfiniteScrollCardView";

const InfiniteScrollTable = ({
  columns = [],
  maxHeight = "65vh",
  maxWidth = "100%",
  fetchSize = 20,
  apiUrl,
  apiParams,
  setState,
  initialState,
  enabled = false,
  setEnabled = () => {},
  filterRefresh = false,
  refresh = false,
  sessionDependent = false,
  selectedSession = null,
  setTableData,
  ...props
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");

  const tableContainerRef = useRef(null);
  const rowVirtualizerInstanceRef = useRef(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState();
  const [sorting, setSorting] = useState([]);

  // Infinite query to fetch paginated data
  const {
    data,
    fetchNextPage,
    isError,
    isFetching,
    isLoading,
    hasNextPage,
    refetch: refetchData,
    remove,
  } = useInfiniteQuery({
    queryKey: [
      "table-data",
      apiParams,
      enabled,
      sessionDependent && selectedSession,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiUrl({
        ...apiParams,
        page: pageParam,
        size: fetchSize,
        // columnFilters,
        // globalFilter,
        // sorting,
      });
      return response?.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined;
    },
    onSuccess: (data) => {
      // Only call setTableData if it's provided as a prop
      if (setTableData && typeof setTableData === "function") {
        setTableData(data.pages[0]);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message ?? "Error loading data");
    },
    refetchOnWindowFocus: false,
  });

  const flatData = useMemo(
    () =>
      data?.pages
        ? data.pages.flatMap((page) => page?.list ?? page?.template)
        : [],
    [data]
  );

  // Function to fetch more data when reaching the bottom of the table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement && hasNextPage) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 100 && !isFetching) {
          // Trigger next page fetch
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetching]
  );

  // Reset the scroll position when filters or sorting are changed
  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      console.error(error);
    }
  }, [sorting, columnFilters, globalFilter]);
  useEffect(() => {
    return () => {
      remove();
    };
  }, [remove]);

  useEffect(() => {
    if (refresh) {
      refetchData();
      setState((prev) => ({
        ...prev,
        refetchData: false,
      }));
    }
  }, [refresh, refetchData, setState]);

  useEffect(() => {
    const containerRefElement = tableContainerRef.current;
    if (containerRefElement) {
      const onScroll = () => {
        fetchMoreOnBottomReached(containerRefElement);
      };
      containerRefElement.addEventListener("scroll", onScroll);
      return () => containerRefElement.removeEventListener("scroll", onScroll);
    }
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    if (setState) {
      setState((prev) => ({
        ...prev,
        tableData: flatData,
        totalTableData: data?.pages?.[0]?.total ?? data?.pages?.[0]?.count ?? 0,
      }));
      setPage(data?.pages?.length ?? 1);
      setPages(data?.pages[0]?.pages ?? 1);
    }
    return () => {};
  }, [data?.pages, flatData, setState]);

  const table = useMaterialReactTable({
    columns,
    data: flatData,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enablePagination: false,
    enableRowNumbers: false,
    enableTopToolbar: true,
    enableRowVirtualization: true,
    enableBottomToolbar: true,
    manualFiltering: false,
    manualSorting: false,
    columnVirtualizerOptions: { overscan: 4 },
    initialState: initialState, //for column pinning
    muiTableProps: {
      sx: {
        tableLayout: isMobile ? "auto" : "fixed",
      },
    },
    muiTablePaperProps: {
      sx: {
        maxWidth: isMobile || isTablet ? "100%" : maxWidth,
        boxShadow: 0,
        border: "1px solid #B6C8D6",
        borderRadius: "10px",
        marginBottom: 2,
      },
    },
    muiTopToolbarProps: {
      sx: {
        borderRadius: "10px 10px 0 0",
        backgroundColor: "#f6f8fc",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: maxHeight,
        overflowY: "auto",
        borderRadius: "0 0 10px 10px",
        "&::-webkit-scrollbar": {
          width: 10,
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-track": {
          border: "1px solid #48657c",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#465f72",
          borderRadius: "10px",
        },
      },
      // onScroll: is handled through useEffect
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: isMobile ? ".9rem" : "12px",
        color: "#0C2F49",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: isMobile ? "1rem" : "14px",
        color: "#0C2F49",
        textAlign: "center",
        marginX: "auto",
        marginY: "auto",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        height: "50px",
        width: "100%",
        backgroundColor: "#F6F8FC",
      },
    },
    enableColumnFilters: true,
    muiTableBodyProps: {
      sx: {
        fontSize: "14px",
      },
    },
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    renderBottomToolbarCustomActions: () => (
      <Typography>
        {/* showing page from the total pages */}
        Showing page {page ?? 0} of pages {pages ?? 0}
      </Typography>
    ),
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      showProgressBars: isFetching,
      sorting,
    },
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 4 },
    ...props,
  });

  if (isMobile) {
    return (
      <InfiniteScrollCardView
        columns={columns}
        fetchSize={fetchSize}
        apiUrl={apiUrl}
        apiParams={apiParams}
        setState={setState}
        enabled={enabled}
        setEnabled={setEnabled}
        refresh={refresh}
        sessionDependent={sessionDependent}
        selectedSession={selectedSession}
        setTableData={setTableData}
        {...props}
      />
    );
  }

  return <MaterialReactTable table={table} />;
};

export default InfiniteScrollTable;
